import { Badge } from "@mantine/core";
import { DataTableColumn } from "mantine-datatable";
import { useMemo } from "react";

import { TableData, TableRow } from "../../../components/Table/types";
import { toLocalDate } from "../../../utils/date";
import { toHumanReadable } from "../../../utils/string";
import { DocumentDatabaseEntry, DocumentDatabaseStatus } from "../types";

interface Props {
  data: DocumentDatabaseEntry[] | null | undefined;
}

export type ExtendedTableRow = TableRow & {
  rawStatus: DocumentDatabaseStatus;
  documentType: string;
  companyName: string;
  fileName?: string;
};

export function useIDPDatabaseTable({ data }: Props): TableData {
  const columns: DataTableColumn<TableRow>[] = [
    {
      accessor: "id",
      hidden: true,
    },
    {
      accessor: "requestDate",
      title: "Request Date",
      sortable: true,
      width: 150,
    },
    {
      accessor: "updatedAt",
      title: "Last Update",
      sortable: true,
      width: 150,
    },
    {
      accessor: "documentType",
      title: "Document Type",
      sortable: true,
      width: 300,
    },
    {
      accessor: "companyName",
      title: "Company Name",
      sortable: true,
      width: 300,
    },
    {
      accessor: "status",
      title: "Status",
      sortable: true,
      width: 250,
    },
  ];

  const getStatusBadge = (status: DocumentDatabaseStatus) => {
    const statusConfig = {
      [DocumentDatabaseStatus.AVAILABLE]: {
        color: "green",
      },
      [DocumentDatabaseStatus.UNREADABLE]: {
        color: "updated",
      },
      [DocumentDatabaseStatus.PROCESSING]: {
        color: "blue",
      },
      [DocumentDatabaseStatus.FAILED]: {
        color: "red",
      },
    };

    const config = statusConfig[status];
    return (
      <Badge color={config.color} size="sm">
        {toHumanReadable(status)}
      </Badge>
    );
  };

  const rows = useMemo(() => {
    if (!data) return [];

    return data.map(
      (item): ExtendedTableRow => ({
        id: item.id,
        requestDate: toLocalDate(item.requestDate),
        documentType: toHumanReadable(item.documentType),
        companyName: item.companyName,
        status: getStatusBadge(item.status),
        sector: item.sector,
        subSector: item.subSector,
        updatedAt: toLocalDate(item.updatedAt),
        rawStatus: item.status,
        fileName: item.companyName,
      })
    ) as TableRow[];
  }, [data]);

  return [columns, rows];
}
